import type { AppContext } from 'types/common'
import type { ComponentProps } from 'svelte'

import component from './Group.svelte'
import { graphql } from '$houdini'
import { v4 as uuid } from '@lukeed/uuid'

export default ({ stateRouter }: AppContext) => {
	stateRouter.addState({
		name: 'app.configuration.group',
		route: 'group',
		querystringParameters: ['lastResetTime', 'lastSavedTime'],
		template: { svelte: true, component },
		async resolve(): Promise<Omit<ComponentProps<typeof component>, 'asr'>> {
			const { data } = await dataQuery.fetch()
			const groups = data?.groups ?? []
			const permissions = data?.permissions ?? []
			const permissionMap = new Map(permissions.map(permission => [permission.id, permission]))
			const userAccounts = data?.userAccounts ?? []
			return {
				groups: groups.map(
					(group) => ({
						...group,
						// group.userAccounts is a SPARSE list, so we have to map over the full userAccounts list
						userAccounts: userAccounts.map(userAccount => ({
							id: userAccount.id,
							name: userAccount.name,
							isMember: userAccount.groups?.some(userGroup => userGroup.id === group.id) ?? false,
						})),
						// group.permissions is an EXHAUSTIVE list, but it needs to be flattened and filled out for consumption in the `UserGroup` component.
						permissions: group.permissions.map(groupPermission => {
							const permission = permissionMap.get(groupPermission.permission.id)
							if (!permission) {
								// This should never happen if the API is working correctly
								throw new Error(`Permission with ID ${groupPermission.permission.id} not found in permission map`)
							}
							return {
								...permission,
								value: groupPermission.value,
							}
						}),
						uuid: uuid(),
					}),
				),
				permissions,
				userAccounts,
			}
		},
	})
}

const dataQuery = graphql(`
	query GroupConfigData {
		groups {
			id
			name
			permissions {
				value: level
				permission {
					id
				}
			}
		}
		permissions {
			id
			category
			codeName
			description
			displayName
		}
		userAccounts {
			id
			name
			groups {
				id
			}
		}
	}
`)
